import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MusicNotesSimple, Eye, EyeSlash, Check, ArrowLeft } from "@phosphor-icons/react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [promoterCode, setPromoterCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !phone || !password) return toast.error("Llena todos los campos");
    if (password.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    if (promoterCode && !/^\d{4}$/.test(promoterCode)) return toast.error("El número de promotor debe ser de 4 dígitos");
    if (!acceptedTerms) return toast.error("Debes aceptar los Términos y el Aviso de Privacidad");
    setLoading(true);
    try {
      await register(phone, password, name, promoterCode.trim());
      toast.success("¡Cuenta creada! Ahora activa tu primera canción.");
      nav("/payment");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrarse");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <div className="max-w-md w-full mx-auto px-4 py-10 flex-1 flex flex-col justify-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 w-fit px-3 py-2 -ml-3 rounded-full hover:bg-white/5 transition-colors"
          data-testid="register-back-home"
        >
          <ArrowLeft size={16} weight="bold" /> Volver al inicio
        </Link>
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="register-brand">
          <div className="w-10 h-10 rounded-full bg-[#FFB800] flex items-center justify-center">
            <MusicNotesSimple weight="fill" size={22} className="text-black" />
          </div>
          <span className="font-display font-bold text-2xl">Tu MusiKa</span>
        </Link>
        <h1 className="font-display text-3xl font-bold mb-1">Crear cuenta</h1>
        <p className="text-white/50 mb-8">Empieza tu primera canción en 5 minutos.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Tu nombre</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Como aparecerá en tu canción"
              className="w-full px-4 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#FFB800] focus:outline-none transition-colors text-white placeholder-white/30"
              data-testid="register-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Teléfono</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="55 1234 5678 (10 dígitos)"
              className="w-full px-4 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#FFB800] focus:outline-none transition-colors text-white placeholder-white/30"
              data-testid="register-phone"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Contraseña</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="w-full px-4 pr-12 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#FFB800] focus:outline-none transition-colors text-white placeholder-white/30"
                data-testid="register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                data-testid="register-toggle-password"
              >
                {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
              </button>
            </div>
          </div>
          <div className="rounded-xl bg-[#0d0d0d] border border-dashed border-[#B4F34A]/30 p-4">
            <label className="text-xs uppercase tracking-widest text-[#B4F34A] mb-2 block">
              ¿Tienes el número de tu promotor? Ponlo aquí
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={promoterCode}
              onChange={(e) => setPromoterCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="4 dígitos (opcional)"
              className="w-full px-4 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#B4F34A] focus:outline-none transition-colors text-white placeholder-white/30 tracking-[0.3em] font-mono"
              data-testid="register-promoter-code"
            />
            <p className="text-[11px] text-white/40 mt-2">Este campo no es obligatorio.</p>
          </div>
          <div className="pt-2">
            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="register-accept-terms-label">
              <button
                type="button"
                onClick={() => setAcceptedTerms(v => !v)}
                aria-pressed={acceptedTerms}
                aria-label="Aceptar términos y aviso de privacidad"
                className={`shrink-0 w-5 h-5 mt-0.5 rounded-md border flex items-center justify-center transition-colors ${acceptedTerms ? "bg-[#FFB800] border-[#FFB800]" : "bg-[#121212] border-white/20 hover:border-white/40"}`}
                data-testid="register-accept-terms-checkbox"
              >
                {acceptedTerms && <Check size={14} weight="bold" className="text-black" />}
              </button>
              <span className="text-sm text-white/70 leading-snug">
                He leído y acepto los{" "}
                <Link to="/terminos" target="_blank" rel="noopener noreferrer" className="text-[#FFB800] hover:underline font-medium" data-testid="register-link-terms">
                  Términos del Servicio (leer)
                </Link>{" "}
                y el{" "}
                <Link to="/aviso-de-privacidad" target="_blank" rel="noopener noreferrer" className="text-[#FFB800] hover:underline font-medium" data-testid="register-link-privacy">
                  Aviso de Privacidad (leer)
                </Link>.
              </span>
            </label>
          </div>
          <button
            type="submit"
            disabled={loading || !acceptedTerms}
            className="w-full py-4 rounded-full bg-[#FFB800] hover:bg-[#E6A600] disabled:opacity-40 disabled:cursor-not-allowed text-black font-semibold transition-all active:scale-95"
            data-testid="register-submit"
          >
            {loading ? "Creando…" : "Crear cuenta"}
          </button>
        </form>
        <p className="text-center text-sm text-white/50 mt-6">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" className="text-[#FFB800] hover:underline" data-testid="register-go-login">
            Iniciar sesión
          </Link>
        </p>
      </div>
    </div>
  );
}
